import React from "react";
import { Box, TablePagination } from "@mui/material";

/**
 * Shared application pagination.
 * Consistent with DataGrid footer: "Rows per page" + range + arrow buttons.
 *
 * Props (backward compatible with older Previous/Next usage):
 * - page, filteredRows, maxRowsPerPage/rowsPerPage, onPageChange
 * Optional: count, hasMore, onRowsPerPageChange, rowsPerPageOptions
 */
const CustomPagination = ({
  page = 0,
  filteredRows = 0,
  maxRowsPerPage,
  rowsPerPage,
  count,
  hasMore,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 20, 30, 50, 100],
  sx = {},
}) => {
  const pageSize = rowsPerPage || maxRowsPerPage || 20;

  const resolvedCount = (() => {
    if (typeof count === "number") return count;

    const loaded = page * pageSize + filteredRows;
    const canGoNext =
      typeof hasMore === "boolean" ? hasMore : filteredRows >= pageSize;

    if (canGoNext) return -1;
    return loaded;
  })();

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "flex-end",
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        ...sx,
      }}
    >
      <TablePagination
        component="div"
        count={resolvedCount}
        page={page}
        onPageChange={(_event, nextPage) => {
          if (typeof onPageChange === "function") {
            onPageChange(nextPage);
          }
        }}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          const nextSize = parseInt(event.target.value, 10);
          if (typeof onRowsPerPageChange === "function") {
            onRowsPerPageChange(nextSize);
            return;
          }
          // Keep page API usable when size change isn't handled by parent.
          if (typeof onPageChange === "function") {
            onPageChange(0);
          }
        }}
        rowsPerPageOptions={
          onRowsPerPageChange ? rowsPerPageOptions : [pageSize]
        }
        labelRowsPerPage="Rows per page:"
        showFirstButton={false}
        showLastButton={false}
        sx={{
          overflow: "hidden",
          ".MuiTablePagination-toolbar": {
            minHeight: 52,
            pl: 2,
            pr: 1,
          },
          ".MuiTablePagination-actions": {
            display: "flex",
            alignItems: "center",
            ml: 1,
          },
          ".MuiTablePagination-actions .MuiIconButton-root": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            mx: 0.25,
            width: 32,
            height: 32,
          },
          ".MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
            opacity: 0.45,
          },
        }}
      />
    </Box>
  );
};

export default CustomPagination;
